import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { lookupBatch } from "@/lib/batches.functions";

export const Route = createFileRoute("/verify")({
  head: () => ({
    meta: [
      { title: "Verify your batch. TruWe lab test report" },
      {
        name: "description",
        content:
          "Enter the batch number printed on your TruWe bottle to view the third-party lab test certificate (COA) for that specific batch.",
      },
      { property: "og:title", content: "Verify your batch. TruWe" },
      {
        property: "og:description",
        content:
          "Every TruWe bottle is third-party tested. Enter your batch number to see the certificate.",
      },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: VerifyPage,
});

type Result =
  | { state: "idle" }
  | { state: "loading" }
  | { state: "not_found"; batch: string }
  | { state: "error"; message: string }
  | {
      state: "found";
      batch: {
        batch_number: string;
        product_handle: string;
        product_name: string | null;
        tested_on: string | null;
        mfg_date: string | null;
        expiry_date: string | null;
        lab_name: string | null;
        coa_signed_url: string;

      };
    };

function fmt(d: string | null): string {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

function VerifyPage() {
  const doLookup = useServerFn(lookupBatch);
  const [input, setInput] = useState("");
  const [res, setRes] = useState<Result>({ state: "idle" });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const q = input.trim();
    if (!q) return;
    setRes({ state: "loading" });
    try {
      const r = await doLookup({ data: { batch: q } });
      if (!r.found) return setRes({ state: "not_found", batch: q });
      setRes({ state: "found", batch: r.batch });
    } catch (err) {
      setRes({
        state: "error",
        message: err instanceof Error ? err.message : "Something went wrong",
      });
    }
  }

  return (
    <div className="min-h-screen bg-[#F7F7F5] text-[#2C2C2C]">
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.28em] text-[#1E5EFF] font-semibold mb-3">
            THIRD-PARTY TESTED
          </p>
          <h1 className="text-3xl md:text-4xl font-semibold mb-3">
            Verify your bottle
          </h1>
          <p className="text-[#2C2C2C]/70 max-w-md mx-auto">
            Enter the batch number printed on the base of your TruWe bottle to
            view the certificate of analysis (COA) for that exact batch.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white border border-[#D9D9D6] rounded-2xl p-5 md:p-6 shadow-sm"
        >
          <label
            htmlFor="batch"
            className="block text-xs font-medium tracking-wide text-[#2C2C2C]/70 mb-2"
          >
            BATCH NUMBER
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              id="batch"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. TW-MAP-2607-01"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              className="flex-1 rounded-lg border border-[#D9D9D6] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#1E5EFF]/40 focus:border-[#1E5EFF]"
            />
            <button
              type="submit"
              disabled={res.state === "loading" || !input.trim()}
              className="rounded-lg bg-[#1E5EFF] text-white font-medium px-6 py-3 hover:bg-[#1849c7] disabled:opacity-50 transition"
            >
              {res.state === "loading" ? "Checking…" : "View report"}
            </button>
          </div>
          <p className="mt-3 text-xs text-[#2C2C2C]/60">
            Not case sensitive. If the number doesn't match, contact
            care@truwe.in.
          </p>
        </form>

        <div className="mt-8">
          {res.state === "not_found" && (
            <div className="bg-white border border-[#D9D9D6] rounded-2xl p-6 text-center">
              <h2 className="font-semibold text-lg mb-1">
                We couldn't find that batch
              </h2>
              <p className="text-sm text-[#2C2C2C]/70">
                No published report matches <b>{res.batch}</b>. Double-check the
                number or write to us at{" "}
                <a
                  className="text-[#1E5EFF] underline"
                  href="mailto:care@truwe.in"
                >
                  care@truwe.in
                </a>
                .
              </p>
            </div>
          )}

          {res.state === "error" && (
            <div className="bg-white border border-red-200 rounded-2xl p-6 text-center">
              <h2 className="font-semibold text-lg mb-1 text-red-700">Error</h2>
              <p className="text-sm text-[#2C2C2C]/70">{res.message}</p>
            </div>
          )}

          {res.state === "found" && (
            <div className="bg-white border border-[#D9D9D6] rounded-2xl overflow-hidden shadow-sm">
              <div className="p-6 border-b border-[#D9D9D6]">
                <p className="text-xs tracking-[0.2em] font-semibold text-[#1E5EFF]">
                  ✓ VERIFIED BATCH
                </p>
                <h2 className="mt-1 text-xl font-semibold">
                  {res.batch.product_name || res.batch.product_handle}
                </h2>
                <p className="text-sm text-[#2C2C2C]/60">
                  Batch{" "}
                  <span className="font-mono font-medium text-[#2C2C2C]">
                    {res.batch.batch_number}
                  </span>
                </p>
              </div>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4 p-6 text-sm">
                <div>
                  <dt className="text-[#2C2C2C]/50 text-xs">Tested on</dt>
                  <dd className="font-medium">{fmt(res.batch.tested_on)}</dd>
                </div>
                <div>
                  <dt className="text-[#2C2C2C]/50 text-xs">Lab</dt>
                  <dd className="font-medium">{res.batch.lab_name || "-"}</dd>
                </div>
                <div>
                  <dt className="text-[#2C2C2C]/50 text-xs">Manufactured</dt>
                  <dd className="font-medium">{fmt(res.batch.mfg_date)}</dd>
                </div>
                <div>
                  <dt className="text-[#2C2C2C]/50 text-xs">Best before</dt>
                  <dd className="font-medium">{fmt(res.batch.expiry_date)}</dd>
                </div>
              </dl>

              <div className="p-6 pt-0">
                <a
                  href={res.batch.coa_signed_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center rounded-lg bg-[#2C2C2C] text-white font-medium px-6 py-3 hover:bg-black transition"
                >
                  Open certificate (PDF) ↗
                </a>
                <p className="mt-2 text-[11px] text-[#2C2C2C]/50 text-center">
                  Link expires in a few minutes for security. Refresh this page
                  to generate a new link.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
